/* ══════════════════════════════════════════════════════════════════════
   Wrenshelf — theme.js
   Vanilla, no dependencies, loaded with `defer` from layout/theme.liquid.

   Ported from the prototype's three inline scripts. Deleted on the way:
     · the localStorage basket        → Shopify AJAX Cart API
     · the currency switcher + its    → Shopify Markets
       hard-coded exchange-rate table
     · the 16 hard-coded products     → Liquid renders every product
     · the "coming with the full      → the pages are real now
       build" placeholder-link toast

   Customer-visible strings NEVER live in this file. They come from a
   JSON blob rendered by Liquid out of locales/en.default.json:

     <script type="application/json" id="wrenshelf-strings">{ … }</script>

   The English constants in DEFAULT_STRINGS below are a last-resort
   fallback so the file still behaves if that blob is missing; they are
   not the source of truth. Same for #wrenshelf-config.

   REQUIRED head boot script (inline in layout/theme.liquid, BEFORE the
   stylesheet — it must run before first paint or the reveals will flash
   in and the hero curtain will lift on a page that has not arrived):

     <script>
     document.documentElement.classList.add('js');
     (function(){
       var m = null;
       try { m = localStorage.getItem('wrenshelf-motion'); } catch(e){}
       if (m ? m === 'off' : window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
         document.documentElement.classList.add('reduce-motion');
       }
     })();
     </script>

   The `js` class is doing two jobs: it gates every "hide until revealed"
   rule, and it is how the stylesheet knows this script ran at all — the
   prefers-reduced-motion safety net at the bottom of theme-custom.css is
   scoped to html:not(.js) so it only takes over when this is missing.
   ══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduceMotion = root.classList.contains('reduce-motion');
  var supportsIO = 'IntersectionObserver' in window;

  /* ────────────────────────────────────────────────────────────────────
     0. Small helpers
     ──────────────────────────────────────────────────────────────────── */

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function on(el, type, fn, opts) { if (el) el.addEventListener(type, fn, opts); }

  /* Read a JSON <script> blob, never throwing on malformed content. */
  function readJson(id) {
    var el = document.getElementById(id);
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
  }

  /* Re-trigger a CSS animation that is already on the element. */
  function replayClass(el, cls) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth; /* force reflow so the animation restarts */
    el.classList.add(cls);
    /* DESIGN.md trap #3 and #4: every one-shot class removes itself.
       `animation-fill-mode: both` otherwise pins the final frame and
       silently disables the scroll reveal (or replays on every insert)
       for the rest of the session. */
    el.addEventListener('animationend', function handler() {
      el.classList.remove(cls);
      el.removeEventListener('animationend', handler);
    });
  }

  /* Shopify CDN images take a width parameter. Never a crop parameter —
     DESIGN.md's plate/mount work depends on uncropped art. */
  function sizedImage(url, width) {
    if (!url) return '';
    if (/[?&]width=/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* ────────────────────────────────────────────────────────────────────
     1. Config, strings, money
     ──────────────────────────────────────────────────────────────────── */

  var CONFIG = readJson('wrenshelf-config');
  var STRINGS = readJson('wrenshelf-strings');

  /* layout/theme.liquid publishes its config as a `window.theme` object
     with [[token]] placeholders instead of the #wrenshelf-* JSON blobs.
     Both shapes are supported. window.theme is read first and the JSON
     blobs are layered on top, so an individual section can add keys this
     file needs (shelf.*, gallery.*, bundle.*) or override one locally
     without the layout having to know about it. */
  var THEME = window.theme || {};
  ['moneyFormat', 'freeShippingThreshold', 'currency', 'routes'].forEach(function (key) {
    if (CONFIG[key] === undefined && THEME[key] !== undefined) CONFIG[key] = THEME[key];
  });

  /* window.theme.strings uses camelCase names. Map them onto the dotted
     keys used below; anything unmapped falls through to DEFAULT_STRINGS. */
  var THEME_STRING_KEYS = {
    'cart.added': 'cartAddedToCart',
    'cart.error': 'cartError',
    'cart.item_count_one': 'cartLabelOne',
    'cart.item_count_other': 'cartLabelOther',
    'cart.qty': 'cartQuantity',
    'cart.remove': 'cartRemove',
    'cart.remove_aria': 'cartRemoveAria',
    'cart.increase': 'cartIncrease',
    'cart.decrease': 'cartDecrease',
    'cart.free': 'cartFree',
    'cart.ship_progress': 'shippingProgress',
    'cart.ship_unlocked': 'shippingUnlocked',
    'menu.open': 'menuOpen',
    'menu.close': 'menuClose',
    'motion.turn_on': 'motionTurnOn',
    'motion.turn_off': 'motionTurnOff',
    'motion.resumed': 'motionToastOn',
    'motion.paused': 'motionToastOff'
  };
  Object.keys(THEME_STRING_KEYS).forEach(function (dotted) {
    var camel = THEME_STRING_KEYS[dotted];
    if (STRINGS[dotted] === undefined && THEME.strings && THEME.strings[camel]) {
      STRINGS[dotted] = THEME.strings[camel];
    }
  });

  var DEFAULT_STRINGS = {
    'cart.item_count_one': 'Open cart, {{ count }} item',
    'cart.item_count_other': 'Open cart, {{ count }} items',
    'cart.qty': 'Qty {{ count }}',
    'cart.increase': 'Increase quantity of {{ title }}',
    'cart.decrease': 'Decrease quantity of {{ title }}',
    'cart.remove': 'Remove',
    'cart.remove_aria': 'Remove {{ title }} from cart',
    'cart.free': 'FREE',
    'cart.ship_progress': 'Add {{ amount }} more for free tracked shipping',
    'cart.ship_unlocked': 'Free tracked shipping unlocked!',
    'cart.added': '“{{ title }}” added to your cart',
    'cart.updated': 'Cart updated',
    'cart.error': 'Sorry — we could not update your cart. Please try again.',
    'shelf.all': 'All {{ count }} books',
    'shelf.filtered_one': '{{ name }}: 1 book on this shelf right now',
    'shelf.filtered_other': '{{ name }}: {{ count }} books on this shelf right now',
    'shelf.none': 'No books on this shelf yet — try another one.',
    'motion.turn_on': 'Turn animations on',
    'motion.turn_off': 'Turn animations off',
    'motion.paused': 'Animations paused for this visit',
    'motion.resumed': 'Animations on for this visit',
    'menu.open': 'Open menu',
    'menu.close': 'Close menu',
    'gallery.view_photo': 'View photo {{ index }} of {{ total }}',
    'bundle.add': 'Add to cart — {{ price }}',
    'bundle.save': 'SAVE {{ percent }}%'
  };

  /* t('cart.qty', { count: 3 }) → "Qty 3"
     Both placeholder styles are understood: {{ token }} (the JSON blob
     convention) and [[token]] (what layout/theme.liquid emits, because a
     Liquid `| t:` call has already consumed the {{ }} braces). */
  function t(key, vars) {
    var str = STRINGS[key];
    if (typeof str !== 'string') str = DEFAULT_STRINGS[key];
    if (typeof str !== 'string') return '';
    if (!vars) return str;
    return str.replace(/\{\{\s*(\w+)\s*\}\}|\[\[\s*(\w+)\s*\]\]/g, function (m, curly, square) {
      var name = curly || square;
      return Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : m;
    });
  }

  var ROUTES = CONFIG.routes || {};
  var CART_URL = ROUTES.cart || '/cart';
  var CART_ADD_URL = ROUTES.cartAdd || '/cart/add';
  var CART_CHANGE_URL = ROUTES.cartChange || '/cart/change';
  var FREE_SHIP_CENTS = typeof CONFIG.freeShippingThreshold === 'number'
    ? CONFIG.freeShippingThreshold
    : 3500;

  /* Shopify's standard money formatter. The pattern comes from
     {{ shop.money_format }} so Markets' presentment currency, decimal
     style and symbol placement all stay Shopify's decision, not ours. */
  var moneyFormat = CONFIG.moneyFormat || '${{amount}}';
  var cartCurrency = CONFIG.currency || null;

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null) return '0';
    var number = (cents / 100).toFixed(precision);
    var parts = number.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents) {
    cents = Math.round(Number(cents) || 0);
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(moneyFormat);
    if (!match) {
      /* money_format had no placeholder (or no config at all) — fall back
         to the browser's own currency formatting. */
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency: cartCurrency || 'USD'
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }
    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ','); break;
      default: value = formatWithDelimiters(cents, 2, ',', '.'); break;
    }
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ────────────────────────────────────────────────────────────────────
     2. Scroll reveal — RE-RUNNABLE
     Liquid renders most of the page, but the cart drawer, the bundle
     widget and any section the theme editor swaps in appear later. The
     prototype ran one query at startup, which left 14 .reveal nodes
     unobserved and stuck at opacity:0 forever. observeReveals() is
     therefore idempotent (data-ro guard) and safe to call any number of
     times, from anywhere.
     ──────────────────────────────────────────────────────────────────── */

  var revealIO = null;
  if (!reduceMotion && supportsIO) {
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('revealed');
        revealIO.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
  }

  function observeReveals(scope) {
    qsa('.reveal:not(.revealed)', scope).forEach(function (el) {
      if (el.dataset.ro) return;   /* already handed to the observer */
      el.dataset.ro = '1';
      if (revealIO) revealIO.observe(el);
      else el.classList.add('revealed');
    });
  }

  /* ────────────────────────────────────────────────────────────────────
     3. Hero curtain
     ONE clock for the whole entrance: every actor starts when the hero
     scene itself becomes visible. Never window.load — on a slow network
     that split the choreography into two shows, the headline finishing
     while the illustration was still empty. The 2.5s failsafe means the
     scene can never stay hidden, whatever the observer does.
     ──────────────────────────────────────────────────────────────────── */

  function initHeroCurtain() {
    var scene = qs('#hero-scene');
    var raise = function () {
      requestAnimationFrame(function () { root.classList.add('hero-ready'); });
    };
    if (reduceMotion || !scene || !supportsIO) { raise(); return; }

    var io = new IntersectionObserver(function (entries, obs) {
      var visible = entries.some(function (e) { return e.isIntersecting; });
      if (!visible) return;
      obs.disconnect();
      raise();
    }, { threshold: 0.12 });
    io.observe(scene);
    setTimeout(raise, 2500);
  }

  /* ────────────────────────────────────────────────────────────────────
     4. Motion toggle  (localStorage key: wrenshelf-motion, "on" | "off")
     ──────────────────────────────────────────────────────────────────── */

  function initMotionToggle() {
    var btn = qs('#motion-btn');
    if (!btn || btn.dataset.wsBound) return;
    btn.dataset.wsBound = '1';

    var off = root.classList.contains('reduce-motion');
    var slash = qs('.motion-off-slash', btn);

    function paint() {
      btn.setAttribute('aria-pressed', String(!off));
      btn.setAttribute('aria-label', off ? t('motion.turn_on') : t('motion.turn_off'));
      if (slash) slash.style.display = off ? '' : 'none';
    }
    paint();

    on(btn, 'click', function () {
      var next = off ? 'on' : 'off';
      var persisted = false;
      try {
        localStorage.setItem('wrenshelf-motion', next);
        persisted = localStorage.getItem('wrenshelf-motion') === next;
      } catch (e) { /* private mode, quota, blocked storage */ }

      if (persisted) { location.reload(); return; }

      /* Storage refused. Reloading would only lose the visitor's place,
         so flip it live for this visit instead. */
      off = !off;
      root.classList.toggle('reduce-motion', off);
      root.classList.toggle('motion-on', !off);
      paint();
      showToast(off ? t('motion.paused') : t('motion.resumed'));
    });
  }

  /* ────────────────────────────────────────────────────────────────────
     5. Header shadow on scroll — one rAF per frame, passive listener
     ──────────────────────────────────────────────────────────────────── */

  function initHeaderShadow() {
    var header = qs('#site-header');
    /* Guarded: initAll() re-runs on every shopify:section:load, and an
       unguarded scroll listener would stack one copy per section swap. */
    if (!header || header.dataset.wsBound) return;
    header.dataset.wsBound = '1';
    var ticking = false;
    function update() {
      header.classList.toggle('header-scrolled', window.scrollY > 8);
      ticking = false;
    }
    update();
    on(window, 'scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
  }

  /* ────────────────────────────────────────────────────────────────────
     6. Mobile menu
     The open AND close animation are pure CSS (@starting-style +
     transition-behavior: allow-discrete). All this does is toggle
     .hidden and keep the button's accessible state honest.
     ──────────────────────────────────────────────────────────────────── */

  function initMobileMenu() {
    var btn = qs('#menu-btn');
    var menu = qs('#mobile-menu');
    if (!btn || !menu || btn.dataset.wsBound) return;
    btn.dataset.wsBound = '1';

    function setState(open) {
      menu.classList.toggle('hidden', !open);
      btn.setAttribute('aria-expanded', String(open));
      btn.setAttribute('aria-label', open ? t('menu.close') : t('menu.open'));
      var openIcon = qs('.menu-open-icon', btn);
      var closeIcon = qs('.menu-close-icon', btn);
      if (openIcon) openIcon.classList.toggle('hidden', open);
      if (closeIcon) closeIcon.classList.toggle('hidden', !open);
    }

    on(btn, 'click', function () {
      setState(menu.classList.contains('hidden'));
    });
    qsa('.mobile-link', menu).forEach(function (a) {
      on(a, 'click', function () { setState(false); });
    });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && !menu.classList.contains('hidden')) {
        setState(false);
        btn.focus();
      }
    });
  }

  /* ────────────────────────────────────────────────────────────────────
     7. Accordions
     Modern browsers do the whole thing in CSS via ::details-content +
     interpolate-size, and that path is left completely alone. This is
     only the fallback for browsers without it: a height transition that
     starts the answer 6px HIGH and settles it DOWN, so it still reads as
     unfolding from under the question rather than a box inflating.
     ──────────────────────────────────────────────────────────────────── */

  var cssHandlesDetails = !!(window.CSS && CSS.supports &&
    (CSS.supports('interpolate-size', 'allow-keywords') ||
     CSS.supports('selector(details::details-content)')));

  function initAccordions(scope) {
    if (cssHandlesDetails || reduceMotion) return;

    qsa('details.acc', scope).forEach(function (det) {
      if (det.dataset.wsAcc) return;
      det.dataset.wsAcc = '1';
      var summary = qs('summary', det);
      var body = Array.prototype.filter.call(det.children, function (c) {
        return c.tagName !== 'SUMMARY';
      })[0];
      if (!summary || !body) return;

      on(summary, 'click', function (e) {
        e.preventDefault();
        var opening = !det.open;
        det.classList.add('acc-animating');

        if (opening) {
          det.open = true;
          var target = body.scrollHeight;
          body.style.height = '0px';
          body.style.opacity = '0';
          body.style.translate = '0 -6px';
          requestAnimationFrame(function () {
            body.style.transition = 'height .34s cubic-bezier(.22,.9,.32,1),opacity .22s ease,translate .34s cubic-bezier(.22,.9,.32,1)';
            body.style.height = target + 'px';
            body.style.opacity = '1';
            body.style.translate = '0 0';
          });
        } else {
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(function () {
            body.style.transition = 'height .28s cubic-bezier(.22,.9,.32,1),opacity .18s ease,translate .28s cubic-bezier(.22,.9,.32,1)';
            body.style.height = '0px';
            body.style.opacity = '0';
            body.style.translate = '0 -6px';
          });
        }

        body.addEventListener('transitionend', function done(ev) {
          if (ev.propertyName !== 'height') return;
          body.removeEventListener('transitionend', done);
          body.style.transition = '';
          body.style.height = '';
          body.style.opacity = '';
          body.style.translate = '';
          det.classList.remove('acc-animating');
          if (!opening) det.open = false;
        });
      });
    });
  }

  /* ────────────────────────────────────────────────────────────────────
     8. Shelf filter
     Filters the rendered card grid client-side on the card's data-shelf
     value. Hides the CARD, never just its image: DESIGN.md trap #1 —
     hiding the <img> left a live grid item behind and blew a 600px hole
     in the layout.
     ──────────────────────────────────────────────────────────────────── */

  function initShelfFilter(scope) {
    var grids = qsa('.shelf-grid', scope);
    if (!grids.length) return;

    qsa('.filter-pill[data-filter]', scope).forEach(function (pill) {
      if (pill.dataset.wsBound) return;
      pill.dataset.wsBound = '1';
      on(pill, 'click', function () { applyShelf(pill.getAttribute('data-filter')); });
    });

    /* Shelf shortcut cards ("Shop by shelf"). These are links that also
       jump to the grid, so the default navigation is left intact. */
    qsa('[data-filter-jump]', scope).forEach(function (el) {
      if (el.dataset.wsBound) return;
      el.dataset.wsBound = '1';
      on(el, 'click', function () { applyShelf(el.getAttribute('data-filter-jump')); });
    });
  }

  function applyShelf(filter) {
    if (!filter) return;
    var grids = qsa('.shelf-grid');
    var count = 0;
    var label = filter;

    grids.forEach(function (grid) {
      qsa('[data-shelf]', grid).forEach(function (card) {
        var show = filter === 'all' || card.getAttribute('data-shelf') === filter;
        card.classList.toggle('hidden', !show);
        if (show) count++;
      });
    });

    qsa('.filter-pill[data-filter]').forEach(function (pill) {
      var active = pill.getAttribute('data-filter') === filter;
      pill.classList.toggle('active', active);
      pill.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (active) {
        /* The shelf's own name, read off the control the visitor just
           pressed — so the status line is translated by whoever
           translated the pill, with no name map in JS. */
        label = pill.getAttribute('data-filter-label') || pill.textContent.trim();
      }
    });

    var message;
    if (filter === 'all') message = t('shelf.all', { count: count });
    else if (count === 0) message = t('shelf.none', { name: label });
    else if (count === 1) message = t('shelf.filtered_one', { name: label, count: count });
    else message = t('shelf.filtered_other', { name: label, count: count });

    qsa('.shelf-status').forEach(function (status) {
      status.textContent = message;
      replayClass(status, 'status-set');
    });

    /* One 260ms settle for the whole surviving set — deliberately NOT a
       stagger, which would just be the AOS wave triggered by a finger.
       replayClass() removes the class again on animationend. */
    grids.forEach(function (grid) { replayClass(grid, 'shelf-settling'); });
  }

  /* ────────────────────────────────────────────────────────────────────
     9. Product gallery
     Liquid renders every thumb with its own image URLs; this only swaps
     which one is showing. Selection is depth, not a coloured ring — see
     the .thumb rules in theme-custom.css.
     ──────────────────────────────────────────────────────────────────── */

  function initGallery(scope) {
    qsa('[data-gallery]', scope).forEach(function (gallery) {
      if (gallery.dataset.wsBound) return;
      gallery.dataset.wsBound = '1';

      var main = qs('[data-gallery-main]', gallery) || qs('#main-image', gallery);
      var thumbs = qsa('.thumb[data-media-src]', gallery);
      if (!main || !thumbs.length) return;

      thumbs.forEach(function (thumb, i) {
        if (!thumb.getAttribute('aria-label')) {
          thumb.setAttribute('aria-label', t('gallery.view_photo', {
            index: i + 1, total: thumbs.length
          }));
        }
        on(thumb, 'click', function () {
          main.src = thumb.getAttribute('data-media-src');
          if (thumb.getAttribute('data-media-srcset')) {
            main.srcset = thumb.getAttribute('data-media-srcset');
          }
          if (thumb.getAttribute('data-media-alt') !== null) {
            main.alt = thumb.getAttribute('data-media-alt');
          }
          thumbs.forEach(function (other) {
            other.classList.toggle('active', other === thumb);
            other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
          });
        });
      });
    });
  }

  /* ────────────────────────────────────────────────────────────────────
     10. Bundle tiers + "complete the bundle" add-on
     The tiers are REAL radio inputs and the add-on is a REAL checkbox —
     Liquid renders them with the prices already worked out server-side,
     in cents. This module only adds up what is checked and keeps the
     price row and the Add button in agreement to the cent.
     ──────────────────────────────────────────────────────────────────── */

  function initBundle(scope) {
    qsa('[data-bundle]', scope).forEach(function (bundle) {
      if (bundle.dataset.wsBound) return;
      bundle.dataset.wsBound = '1';

      var radios = qsa('input[type="radio"][data-tier]', bundle);
      var addon = qs('input[type="checkbox"][data-addon]', bundle);
      var priceEl = qs('[data-bundle-price]', bundle);
      var compareEl = qs('[data-bundle-compare]', bundle);
      var compareWrap = qs('[data-bundle-compare-wrap]', bundle) || compareEl;
      var saveEl = qs('[data-bundle-save]', bundle);
      var addBtn = qs('.add-btn', bundle) || qs('[data-bundle-add]', bundle);
      var addLabel = addBtn ? (qs('[data-bundle-label]', addBtn) || addBtn) : null;

      function num(el, attr) { return parseInt(el.getAttribute(attr), 10) || 0; }

      function update(animate) {
        var tier = radios.filter(function (r) { return r.checked; })[0] || radios[0];
        if (!tier) return;
        tier.checked = true;

        var qty = parseInt(tier.getAttribute('data-qty'), 10) || 1;
        var total = num(tier, 'data-total');
        var compare = num(tier, 'data-compare');

        var extras = [];
        if (addon && addon.checked) {
          total += num(addon, 'data-addon-price');
          compare += num(addon, 'data-addon-compare');
          extras.push({
            id: addon.getAttribute('data-addon-variant'),
            quantity: 1,
            properties: { _bundle: 'addon' }
          });
        }
        if (tier.getAttribute('data-gift-variant')) {
          compare += num(tier, 'data-gift-value');
          extras.push({
            id: tier.getAttribute('data-gift-variant'),
            quantity: 1,
            properties: { _bundle: 'gift' }
          });
        }

        /* Mirror the checked state onto the label as a class as well, so
           the cards still style in a browser without :has(). */
        radios.forEach(function (r) {
          var card = r.closest('.tier-card');
          if (card) card.classList.toggle('selected', r.checked);
        });
        if (addon) {
          var addonCard = addon.closest('.addon-card');
          if (addonCard) addonCard.classList.toggle('selected', addon.checked);
        }

        if (priceEl) priceEl.textContent = formatMoney(total);
        var discounted = compare > total;
        if (compareEl) compareEl.textContent = formatMoney(compare);
        if (compareWrap) compareWrap.classList.toggle('hidden', !discounted);
        if (saveEl) {
          saveEl.classList.toggle('hidden', !discounted);
          if (discounted) {
            saveEl.textContent = t('bundle.save', {
              percent: Math.round((1 - total / compare) * 100)
            });
          }
        }
        if (addBtn) {
          addBtn.setAttribute('data-quantity', String(qty));
          addBtn.setAttribute('data-extra-items', JSON.stringify(extras));
          if (addLabel) addLabel.textContent = t('bundle.add', { price: formatMoney(total) });
        }

        /* The price re-inks only when a person changed it — never on the
           initial paint, or every price on the page flashes on load. */
        if (animate && !reduceMotion) {
          [priceEl, compareEl].forEach(function (el) { replayClass(el, 'price-set'); });
        }
      }

      radios.forEach(function (r) { on(r, 'change', function () { update(true); }); });
      if (addon) on(addon, 'change', function () { update(true); });
      update(false);
    });
  }

  /* ────────────────────────────────────────────────────────────────────
     11. Toast
     ──────────────────────────────────────────────────────────────────── */

  var toastTimer = null;

  function showToast(message) {
    var toast = qs('#toast');
    var msg = qs('#toast-msg') || toast;
    if (!toast || !message) return;
    msg.textContent = message;
    toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.classList.remove('show'); }, 2400);
  }

  /* ────────────────────────────────────────────────────────────────────
     12. Cart — Shopify AJAX Cart API
     /cart/add.js, /cart/change.js, /cart.js. No localStorage anywhere:
     the basket the drawer shows and the basket checkout charges are the
     same object.
     ──────────────────────────────────────────────────────────────────── */

  var cartEls = {};
  var lastFocusBeforeCart = null;
  var inertFallbackBound = false;

  function cacheCartEls() {
    cartEls = {
      drawer: qs('#cart-drawer'),
      backdrop: qs('#cart-backdrop'),
      openBtn: qs('#cart-btn'),
      closeBtn: qs('#cart-close'),
      countBadge: qs('#cart-count'),
      drawerCount: qs('#cart-drawer-count'),
      items: qs('#cart-items'),
      empty: qs('#cart-empty'),
      footer: qs('#cart-footer'),
      subtotal: qs('#cart-subtotal'),
      shipMsg: qs('#cart-ship-msg'),
      shipBar: qs('#cart-ship-bar'),
      browse: qs('#cart-browse')
    };
  }

  function cartFetch(url, options) {
    var opts = options || {};
    opts.headers = opts.headers || {};
    opts.headers.Accept = 'application/json';
    opts.headers['X-Requested-With'] = 'XMLHttpRequest';
    return fetch(url, opts).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var err = new Error(data.description || data.message || t('cart.error'));
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function getCart() { return cartFetch(CART_URL + '.js'); }

  function addToCart(items) {
    return cartFetch(CART_ADD_URL + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    });
  }

  function addFormToCart(form) {
    return cartFetch(CART_ADD_URL + '.js', { method: 'POST', body: new FormData(form) });
  }

  function changeLine(line, quantity) {
    return cartFetch(CART_CHANGE_URL + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    });
  }

  /* ── Rendering the drawer from /cart.js ───────────────────────────── */

  function renderCart(cart) {
    if (!cart) return;
    if (cart.currency && cart.currency !== cartCurrency) cartCurrency = cart.currency;

    var count = cart.item_count || 0;

    if (cartEls.countBadge) cartEls.countBadge.textContent = String(count);
    if (cartEls.openBtn) {
      cartEls.openBtn.setAttribute('aria-label',
        t(count === 1 ? 'cart.item_count_one' : 'cart.item_count_other', { count: count }));
    }
    if (cartEls.drawerCount) cartEls.drawerCount.textContent = count ? '(' + count + ')' : '';

    var hasItems = count > 0;
    if (cartEls.empty) cartEls.empty.classList.toggle('hidden', hasItems);
    if (cartEls.items) cartEls.items.classList.toggle('hidden', !hasItems);
    if (cartEls.footer) cartEls.footer.classList.toggle('hidden', !hasItems);
    if (!hasItems || !cartEls.items) return;

    cartEls.items.innerHTML = '';
    (cart.items || []).forEach(function (item, index) {
      cartEls.items.appendChild(buildCartLine(item, index + 1));
    });

    var subtotal = typeof cart.items_subtotal_price === 'number'
      ? cart.items_subtotal_price
      : cart.total_price;
    if (cartEls.subtotal) cartEls.subtotal.textContent = formatMoney(subtotal);

    if (cartEls.shipMsg && cartEls.shipBar) {
      if (subtotal >= FREE_SHIP_CENTS) {
        cartEls.shipMsg.textContent = t('cart.ship_unlocked');
        cartEls.shipBar.style.width = '100%';
      } else {
        cartEls.shipMsg.textContent = t('cart.ship_progress', {
          amount: formatMoney(FREE_SHIP_CENTS - subtotal)
        });
        cartEls.shipBar.style.width =
          Math.round(subtotal / FREE_SHIP_CENTS * 100) + '%';
      }
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function buildCartLine(item, line) {
    var li = el('li', 'cart-line flex items-start justify-between gap-3');
    li.setAttribute('data-line', String(line));

    var left = el('div', 'flex items-start gap-3 min-w-0');

    var imgUrl = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
    if (imgUrl) {
      var img = document.createElement('img');
      img.src = sizedImage(imgUrl, 112);
      img.alt = '';
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      img.className = 'cart-line-img w-14 h-14 rounded-xl object-cover bg-creamdeep shrink-0';
      left.appendChild(img);
    }

    var info = el('div', 'min-w-0');
    var title = item.product_title || item.title || '';
    info.appendChild(el('p', 'cart-line-title font-display font-semibold text-charcoal leading-snug', title));

    if (item.variant_title && item.variant_title !== 'Default Title') {
      info.appendChild(el('p', 'cart-line-variant text-soft text-xs font-bold mt-0.5', item.variant_title));
    }

    /* A gift line has a zero price and must not be editable — nudging it
       to 0 would silently drop a promised free book. */
    var isFree = item.final_line_price === 0 || item.line_price === 0;

    if (isFree) {
      info.appendChild(el('p', 'cart-line-qty text-soft text-sm font-bold mt-0.5',
        t('cart.qty', { count: item.quantity })));
    } else {
      var stepper = el('div', 'cart-qty mt-1.5');
      var dec = el('button', 'cart-qty-dec', '−');
      dec.type = 'button';
      dec.setAttribute('aria-label', t('cart.decrease', { title: title }));
      var val = el('span', 'cart-qty-val', String(item.quantity));
      val.setAttribute('aria-live', 'polite');
      var inc = el('button', 'cart-qty-inc', '+');
      inc.type = 'button';
      inc.setAttribute('aria-label', t('cart.increase', { title: title }));
      on(dec, 'click', function () { updateLine(line, item.quantity - 1); });
      on(inc, 'click', function () { updateLine(line, item.quantity + 1); });
      stepper.appendChild(dec);
      stepper.appendChild(val);
      stepper.appendChild(inc);
      info.appendChild(stepper);
    }

    left.appendChild(info);

    var right = el('div', 'text-right shrink-0');
    var priceText = isFree
      ? t('cart.free')
      : formatMoney(typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price);
    right.appendChild(el('p', 'cart-line-price font-bold text-charcoal', priceText));

    var remove = el('button',
      'cart-line-remove mt-1 min-h-[32px] text-xs font-extrabold text-coraldark underline underline-offset-2 cursor-pointer',
      t('cart.remove'));
    remove.type = 'button';
    remove.setAttribute('aria-label', t('cart.remove_aria', { title: title }));
    on(remove, 'click', function () { updateLine(line, 0); });
    right.appendChild(remove);

    li.appendChild(left);
    li.appendChild(right);
    return li;
  }

  function updateLine(line, quantity) {
    if (quantity < 0) quantity = 0;
    if (cartEls.items) cartEls.items.setAttribute('aria-busy', 'true');
    changeLine(line, quantity)
      .then(renderCart)
      .catch(function (err) { showToast(err.message || t('cart.error')); })
      .then(function () {
        if (cartEls.items) cartEls.items.removeAttribute('aria-busy');
      });
  }

  /* ── Open / close, focus trap, focus return ───────────────────────── */

  function inertTargets() {
    return Array.prototype.filter.call(document.body.children, function (node) {
      if (node === cartEls.drawer || node === cartEls.backdrop) return false;
      if (node.id === 'toast') return false;
      return ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT'].indexOf(node.tagName) === -1;
    });
  }

  var inertSupported = 'inert' in HTMLElement.prototype;

  function setPageInert(state) {
    if (!inertSupported) return;
    inertTargets().forEach(function (node) {
      if (state) {
        if (node.hasAttribute('inert')) return; /* already inert for another reason */
        node.setAttribute('inert', '');
        node.setAttribute('data-ws-inert', '');
      } else if (node.hasAttribute('data-ws-inert')) {
        node.removeAttribute('inert');
        node.removeAttribute('data-ws-inert');
      }
    });
  }

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
    'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /* Only used where `inert` is unavailable — a plain Tab cycle inside
     the drawer so focus cannot walk out into the hidden page. */
  function trapTab(e) {
    if (e.key !== 'Tab' || !document.body.classList.contains('cart-open')) return;
    if (!cartEls.drawer) return;
    /* getClientRects() rather than offsetParent: the drawer is
       position:fixed, and offsetParent semantics around fixed ancestors
       are exactly the kind of thing that silently returns an empty list. */
    var focusable = qsa(FOCUSABLE, cartEls.drawer).filter(function (n) {
      return n.getClientRects().length > 0;
    });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openCart() {
    if (!cartEls.drawer) return;
    lastFocusBeforeCart = document.activeElement;
    document.body.classList.add('cart-open');
    cartEls.drawer.setAttribute('aria-hidden', 'false');
    if (cartEls.openBtn) cartEls.openBtn.setAttribute('aria-expanded', 'true');
    setPageInert(true);
    if (!inertSupported && !inertFallbackBound) {
      inertFallbackBound = true;
      document.addEventListener('keydown', trapTab, true);
    }
    refreshCart();
    if (cartEls.closeBtn) cartEls.closeBtn.focus();
  }

  function closeCart() {
    if (!cartEls.drawer) return;
    document.body.classList.remove('cart-open');
    cartEls.drawer.setAttribute('aria-hidden', 'true');
    if (cartEls.openBtn) cartEls.openBtn.setAttribute('aria-expanded', 'false');
    setPageInert(false);
    /* Focus goes back where it came from, not to a guessed element. */
    var target = lastFocusBeforeCart && document.contains(lastFocusBeforeCart)
      ? lastFocusBeforeCart
      : cartEls.openBtn;
    if (target && target.focus) target.focus();
    lastFocusBeforeCart = null;
  }

  function refreshCart() {
    return getCart().then(renderCart).catch(function () { /* offline: keep last paint */ });
  }

  function popCartCount() {
    replayClass(cartEls.countBadge, 'cart-pop');
  }

  /* ── Add to basket ────────────────────────────────────────────────── */

  function handleAdd(btn) {
    var form = btn.closest('form[action*="/cart/add"]');
    var extrasAttr = btn.getAttribute('data-extra-items');
    var extras = [];
    if (extrasAttr) {
      try { extras = JSON.parse(extrasAttr) || []; } catch (e) { extras = []; }
    }
    extras = extras.filter(function (x) { return x && x.id; });

    var request;
    if (form && !extras.length) {
      /* A real product form carries variant selects and line-item
         properties — let Shopify parse it rather than rebuilding it. */
      request = addFormToCart(form);
    } else {
      var variantId = btn.getAttribute('data-variant-id') ||
        (form && form.querySelector('[name="id"]') && form.querySelector('[name="id"]').value);
      if (!variantId) return;
      var items = [{
        id: Number(variantId) || variantId,
        quantity: parseInt(btn.getAttribute('data-quantity'), 10) || 1
      }];
      request = addToCart(items.concat(extras));
    }

    btn.setAttribute('aria-busy', 'true');
    request
      .then(function () { return getCart(); })
      .then(function (cart) {
        renderCart(cart);
        popCartCount();
        /* data-product-title is what snippets/product-card.liquid emits;
           data-title is the prototype's name. Accept either, and never
           render the word "undefined" into a toast. */
        showToast(t('cart.added', {
          title: btn.getAttribute('data-product-title') || btn.getAttribute('data-title') || ''
        }));
        if (btn.getAttribute('data-open-cart') !== 'false') openCart();
      })
      .catch(function (err) { showToast(err.message || t('cart.error')); })
      .then(function () { btn.removeAttribute('aria-busy'); });
  }

  function initCart() {
    cacheCartEls();
    if (!cartEls.drawer) return;

    if (!cartEls.drawer.dataset.wsBound) {
      cartEls.drawer.dataset.wsBound = '1';
      /* preventDefault so the basket button can be a real <a href="/cart">
         and still open the drawer when JS is available. */
      on(cartEls.openBtn, 'click', function (e) { e.preventDefault(); openCart(); });
      on(cartEls.closeBtn, 'click', closeCart);
      on(cartEls.backdrop, 'click', closeCart);
      on(cartEls.browse, 'click', closeCart);
      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && document.body.classList.contains('cart-open')) closeCart();
      });
      /* Any other control that wants the drawer. */
      qsa('[data-cart-open]').forEach(function (n) { on(n, 'click', function (e) {
        e.preventDefault();
        openCart();
      }); });
    }

    bindAddButtons();
    refreshCart();
  }

  function bindAddButtons(scope) {
    qsa('.add-btn', scope).forEach(function (btn) {
      if (btn.dataset.wsBound) return;
      btn.dataset.wsBound = '1';
      on(btn, 'click', function (e) {
        e.preventDefault();
        handleAdd(btn);
      });
    });
    /* Product forms submitted with Enter from a field, not the button. */
    qsa('form[action*="/cart/add"]', scope).forEach(function (form) {
      if (form.dataset.wsBound) return;
      form.dataset.wsBound = '1';
      on(form, 'submit', function (e) {
        var btn = qs('.add-btn', form);
        if (!btn) return; /* no AJAX button here — let it post normally */
        e.preventDefault();
        handleAdd(btn);
      });
    });
  }

  /* ────────────────────────────────────────────────────────────────────
     13. Sale countdown
     Counts to [data-countdown-end] (an ISO timestamp) or, with no end
     given, to local midnight — the prototype's behaviour.
     ──────────────────────────────────────────────────────────────────── */

  function initCountdown(scope) {
    qsa('[data-countdown]', scope).forEach(function (box) {
      if (box.dataset.wsBound) return;
      box.dataset.wsBound = '1';

      var h = qs('[data-cd="h"]', box) || qs('#cd-h', box);
      var m = qs('[data-cd="m"]', box) || qs('#cd-m', box);
      var s = qs('[data-cd="s"]', box) || qs('#cd-s', box);
      if (!h || !m || !s) return;

      var endAttr = box.getAttribute('data-countdown-end');
      function pad(n) { return (n < 10 ? '0' : '') + n; }

      function tick() {
        var now = new Date();
        var end;
        if (endAttr) {
          end = new Date(endAttr);
          if (isNaN(end.getTime())) end = null;
        }
        if (!end) end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
        var left = Math.max(0, Math.floor((end - now) / 1000));
        h.textContent = pad(Math.floor(left / 3600));
        m.textContent = pad(Math.floor((left % 3600) / 60));
        s.textContent = pad(left % 60);
      }
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ────────────────────────────────────────────────────────────────────
     14. Boot
     ──────────────────────────────────────────────────────────────────── */

  function initAll(scope) {
    observeReveals(scope);
    initMobileMenu();
    initHeaderShadow();
    initMotionToggle();
    initAccordions(scope);
    initShelfFilter(scope);
    initGallery(scope);
    initBundle(scope);
    initCountdown(scope);
    bindAddButtons(scope);
  }

  function boot() {
    initAll(document);
    initHeroCurtain();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor swaps section markup in and out without a reload.
     Everything above is idempotent, so re-running it on the new subtree
     is all that is needed — and it is what keeps freshly inserted
     .reveal nodes from being stranded at opacity:0. */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    cacheCartEls();
    initCart();
  });
  document.addEventListener('shopify:section:select', function (e) { observeReveals(e.target); });
  document.addEventListener('shopify:block:select', function (e) { observeReveals(e.target); });

  /* Back/forward cache: the basket may have changed in another tab. */
  on(window, 'pageshow', function (e) { if (e.persisted) refreshCart(); });

  /* Public surface for section-level scripts. */
  window.Wrenshelf = {
    observeReveals: observeReveals,
    showToast: showToast,
    formatMoney: formatMoney,
    t: t,
    cart: {
      open: openCart,
      close: closeCart,
      refresh: refreshCart,
      get: getCart,
      add: addToCart,
      change: changeLine,
      render: renderCart
    },
    applyShelf: applyShelf
  };
})();

/* ── Quick-help widget ──────────────────────────────────────────────────
   snippets/help-widget.liquid. No network, no third party: every answer
   is already in the DOM, so this only opens, closes and toggles.

   The unread dot is shown once per SESSION and cleared the first time
   the panel is opened. sessionStorage rather than localStorage on
   purpose — a returning visitor a week later has genuinely not read it
   in this visit, and a dot that never comes back trains people to
   ignore it. Storage can throw in private mode, so every touch is
   wrapped; a failed read simply means no dot, never a broken widget. */
(function () {
  var root = document.querySelector('[data-help-widget]');
  if (!root) return;

  var panel  = root.querySelector('[data-help-panel]');
  var toggle = root.querySelector('[data-help-toggle]');
  var closer = root.querySelector('[data-help-close]');
  var dot    = root.querySelector('[data-help-dot]');
  var iconOpen  = root.querySelector('[data-help-icon-open]');
  var iconClose = root.querySelector('[data-help-icon-close]');
  var scroller  = root.querySelector('[data-help-scroll]');
  if (!panel || !toggle) return;

  var KEY = 'wrenshelf-help-seen';
  function seen(v) {
    try {
      if (v === undefined) return sessionStorage.getItem(KEY) === '1';
      sessionStorage.setItem(KEY, '1');
    } catch (e) {}
    return false;
  }

  if (dot && !seen()) dot.classList.remove('hidden');

  var open = false;

  function setOpen(next) {
    open = next;
    panel.classList.toggle('hidden', !open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (iconOpen)  iconOpen.classList.toggle('hidden', open);
    if (iconClose) iconClose.classList.toggle('hidden', !open);

    if (open) {
      if (dot) dot.classList.add('hidden');
      seen(true);
      /* Focus the close button rather than the panel: it is the first
         thing a keyboard user wants and it announces the dialog title
         through aria-labelledby without stealing the scroll position. */
      if (closer) closer.focus();
    } else {
      toggle.focus();
    }
  }

  toggle.addEventListener('click', function () { setOpen(!open); });
  if (closer) closer.addEventListener('click', function () { setOpen(false); });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); }
  });

  /* Click-away. Pointerdown rather than click so it fires before a link
     inside the panel navigates, and scoped to the widget so the rest of
     the page keeps working normally while the panel is up. */
  document.addEventListener('pointerdown', function (e) {
    if (open && !root.contains(e.target)) setOpen(false);
  });

  /* One question open at a time. The panel is short; two long answers
     turn it into a scroll well and the shopper loses the list. */
  root.querySelectorAll('[data-help-q]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var key = btn.getAttribute('data-help-q');
      var body = root.querySelector('[data-help-a="' + key + '"]');
      if (!body) return;
      var wasOpen = !body.classList.contains('hidden');

      root.querySelectorAll('[data-help-a]').forEach(function (b) { b.classList.add('hidden'); });
      root.querySelectorAll('[data-help-q]').forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
        var chev = b.querySelector('[data-help-chev]');
        if (chev) chev.style.transform = '';
      });

      if (!wasOpen) {
        body.classList.remove('hidden');
        btn.setAttribute('aria-expanded', 'true');
        var chev = btn.querySelector('[data-help-chev]');
        if (chev) chev.style.transform = 'rotate(180deg)';
        /* Bring the newly opened answer into view inside the panel. The
           guard matters: the theme honours prefers-reduced-motion
           everywhere else, and a smooth scroll here would be the one
           place it did not. */
        var smooth = !document.documentElement.classList.contains('reduce-motion');
        if (scroller && btn.parentElement) {
          btn.parentElement.scrollIntoView({
            behavior: smooth ? 'smooth' : 'auto',
            block: 'nearest'
          });
        }
      }
    });
  });
})();
